/**
 * Deterministic PRNG. The whole simulation must be reproducible from
 * (seed + command history) so that saves are small, replays work, and bug
 * reports can be acted on. Never call Math.random() inside src/sim.
 *
 * mulberry32: fast, well enough distributed, and a few lines of 32-bit integer
 * arithmetic that any language reproduces exactly. Carried over from Sable
 * Reserve's `src/sim/rng.ts` (at `e74ac8c`), its draws unchanged (its state
 * held otherwise, below), together with its binomial sampler and the reason
 * for that sampler's shape. `tests/rng.test.ts` pins the
 * first draws, so a port can be checked against them.
 */
export class Rng {
  /**
   * The state, one 32-bit word, kept in a typed array (review M76; Sable
   * Reserve's is a number field, which V8 holds as a heap number, read and
   * written through on every draw, once it leaves the small integers, below
   * 2^30 in a build with pointer compression such as Chrome's and 2^31 in
   * this repository's Node, as a state spread over 2^32 does at once): the
   * same words, so the same draws. MEASURED, review M76's simplifier, a
   * scratch script timing the bullethead's step inside the tick, seed 42, ten
   * years, the median of five per run, three runs each: 39.8 to 42.5 µs a
   * day with the field, 31.1 to 33.5 with the array.
   */
  private readonly state = new Uint32Array(1);

  constructor(seed: number) {
    this.state[0] = seed >>> 0;
  }

  /** Raw state, for saving into the world. */
  get seed(): number {
    return this.state[0] as number;
  }

  static fromState(state: number): Rng {
    return new Rng(state);
  }

  /** Uniform float in [0, 1): one step of mulberry32, its shifts and multipliers as published. */
  next(): number {
    this.state[0] = ((this.state[0] as number) + 0x6d2b79f5) >>> 0;
    let t = this.state[0] as number;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  }

  /** Uniform float in [min, max). */
  range(min: number, max: number): number {
    return min + this.next() * (max - min);
  }

  /** Uniform integer in [min, max]. */
  int(min: number, max: number): number {
    return Math.floor(this.range(min, max + 1));
  }

  /** True with probability p. */
  chance(p: number): boolean {
    return this.next() < p;
  }

  pick<T>(items: readonly T[]): T {
    if (items.length === 0) throw new Error('Rng.pick on empty array');
    return items[Math.floor(this.next() * items.length)] as T;
  }

  /** Approximate standard normal via Box-Muller. */
  normal(mean = 0, stdDev = 1): number {
    const u = Math.max(this.next(), 1e-12);
    const v = this.next();
    return mean + stdDev * Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v);
  }

  /**
   * Number of successes in n independent trials of probability p.
   *
   * Exact for small n; a Poisson draw when the expected count is small; the
   * normal approximation only where it is valid, so that a school of ten
   * thousand does not cost ten thousand draws a day.
   *
   * **The middle branch is the fix for a bug that shaped Sable Reserve.** Its
   * first version used the normal approximation for every n above 24. A daily
   * death probability is of the order of 1e-4 to 1e-3: at n = 40 the mean is a
   * few hundredths with a standard deviation of a tenth, and rounding that
   * gives zero almost every time. MEASURED there over 200,000 draws: herds of
   * 40 and 60 received 0% of their coded natural deaths and daily birth draws
   * came out 26% high, so every herd above 24 head barely died of anything but
   * starvation and over-bred (Sable Reserve M23, M95). The same arithmetic
   * applies to a pod of whales, so the sampler comes over with the fix in it.
   *
   * The normal approximation is sound when np and n(1 - p) are both large; the
   * classical threshold is about 10. Below it a binomial with small p is a
   * Poisson to within the noise, and Knuth's draw costs about np + 1 uniforms.
   */
  binomial(n: number, p: number): number {
    if (n <= 0 || p <= 0) return 0;
    if (p >= 1) return n;
    if (n <= 24) {
      let hits = 0;
      for (let i = 0; i < n; i++) if (this.next() < p) hits++;
      return hits;
    }
    const mean = n * p;
    if (mean < NORMAL_APPROXIMATION_MIN_MEAN) return Math.min(n, this.poisson(mean));
    // The mirror case, drawn as a mirror. When the *failures* are the rare
    // event, the successes are not Poisson: `poisson(n * p)` there is far too
    // dispersed and, past a mean of about 700, truncated by the underflow of
    // Knuth's product. MEASURED in Sable Reserve with the failures unhandled:
    // binomial(1000, 0.995) returned a mean of 745 against a true 995.
    if (n * (1 - p) < NORMAL_APPROXIMATION_MIN_MEAN) {
      return n - Math.min(n, this.poisson(n * (1 - p)));
    }
    const sd = Math.sqrt(n * p * (1 - p));
    return clampInt(Math.round(this.normal(mean, sd)), 0, n);
  }

  /**
   * A Poisson count of any mean (M74): Knuth's draw below the normal
   * approximation's threshold, the binomial's (below), and the normal
   * approximation, rounded and not below 0, from it, where Knuth's product
   * would cost a uniform a count and underflow past a mean of about 700.
   */
  count(mean: number): number {
    if (mean <= 0) return 0;
    if (mean < NORMAL_APPROXIMATION_MIN_MEAN) return this.poisson(mean);
    return Math.max(0, Math.round(this.normal(mean, Math.sqrt(mean))));
  }

  /** Knuth's Poisson draw: exact, and cheap while the mean is small. */
  poisson(mean: number): number {
    const limit = Math.exp(-mean);
    let k = 0;
    let product = this.next();
    while (product > limit) {
      k++;
      product *= this.next();
    }
    return k;
  }
}

/**
 * Below this expected count the normal approximation to a binomial is not one:
 * the distribution is skewed against a hard floor at zero and rounding the
 * draw destroys the mean. ASSERTED: ten, the usual textbook bound (np and
 * n(1 - p) both at least ten), carried over from Sable Reserve.
 */
const NORMAL_APPROXIMATION_MIN_MEAN = 10;

function clampInt(v: number, min: number, max: number): number {
  return v < min ? min : v > max ? max : v;
}
